import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { API_URL, LOCATION_ID } from '..';

const EditStockLayer = () => {
  const { id } = useParams();
  const [formValues, setFormValues] = useState({
    title: '',
    sellPrice: 0,
    costPrice: 0,
    quantity: 0,
    description: '',
    supplier_id: '', // ✅ Supplier field
  });
  const [suppliers, setSuppliers] = useState([]); // ✅ Supplier list
  const [message, setMessage] = useState({
    status: false,
    reason: '',
    desc: '',
  });
  const quillRef = useRef(null);
  const quillInstance = useRef(null);

  // ✅ Fetch suppliers
  const fetchSuppliers = async () => {
    try {
       const locationId = localStorage.getItem('locationID')
      const response = await fetch(`${API_URL}/api/suppliers?locationId=${locationId}`);
      const data = await response.json();
      if (response.ok && data.suppliers) setSuppliers(data.suppliers);
      else setSuppliers([]);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setSuppliers([]);
    }
  };

  // ✅ Fetch Stock Details
  const fetchStock = async () => {
    try {
      const response = await fetch(`${API_URL}/api/invoice/stock/fetch-single/${id}`);
      const data = await response.json();
      if (response.ok) {
        setFormValues({
          title: data.stock.title || '',
          sellPrice: data.stock.sellPrice || 0,
          costPrice: data.stock.costPrice || 0,
          quantity: data.stock.quantity || 0,
          description: data.stock.description || '',
          supplier_id: data.stock.supplier_id || '', // ✅ Set current supplier
        });
      } else {
        throw new Error(data.message || 'Failed to fetch stock');
      }
    } catch (error) {
      setMessage({ status: true, reason: 'error', desc: `Failed to fetch stock: ${error.message}` });
    }
  };

  // ✅ Initialize Quill
  useEffect(() => {
    if (quillRef.current && !quillInstance.current) {
      quillInstance.current = new Quill(quillRef.current, {
        theme: 'snow',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ align: '' }, { align: 'center' }, { align: 'right' }],
          ],
        },
        placeholder: 'Enter stock description',
      });

      quillInstance.current.root.setAttribute('dir', 'ltr');
      quillInstance.current.root.setAttribute('lang', 'en');

      quillInstance.current.on('text-change', () => {
        const content = quillInstance.current.root.innerHTML;
        setFormValues((prev) => ({ ...prev, description: content }));
      });
    }

    return () => {
      if (quillInstance.current) {
        quillInstance.current.off('text-change');
        quillInstance.current = null;
      }
    };
  }, []);

  // ✅ Populate description in Quill after fetch
  useEffect(() => {
    if (quillInstance.current && formValues.description) {
      if (quillInstance.current.root.innerHTML !== formValues.description) {
        quillInstance.current.root.innerHTML = formValues.description;
      }
    }
  }, [formValues.description]);

  useEffect(() => {
    fetchSuppliers();
    fetchStock();
  }, []);

  // ✅ Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formValues.title)
      return setMessage({ status: true, reason: 'error', desc: 'Title is required' });
    if (!formValues.costPrice || formValues.costPrice <= 0)
      return setMessage({ status: true, reason: 'error', desc: 'Buy Price must be greater than 0' });
    if (!formValues.sellPrice || formValues.sellPrice <= 0)
      return setMessage({ status: true, reason: 'error', desc: 'Selling Price must be greater than 0' });
    if (!formValues.quantity || formValues.quantity <= 0)
      return setMessage({ status: true, reason: 'error', desc: 'Quantity must be greater than 0' });
    if (!formValues.description || formValues.description === '<p><br></p>')
      return setMessage({ status: true, reason: 'error', desc: 'Description is required' });
    if (!formValues.supplier_id)
      return setMessage({ status: true, reason: 'error', desc: 'Supplier selection is required' });

    try {
      const response = await fetch(`${API_URL}/api/invoice/stock/update-single/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formValues),
      });
      const data = await response.json();

      if (response.ok) {
        setMessage({ status: true, reason: 'success', desc: 'Stock updated successfully' });
      } else {
        throw new Error(data.message || 'Failed to update stock');
      }
    } catch (error) {
      setMessage({ status: true, reason: 'error', desc: `Failed to update stock: ${error.message}` });
    }
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-body p-24">
        <div className="row justify-content-center">
          <div className="col-xxl-6 col-xl-8 col-lg-10">
            <div className="card border">
              <div className="card-body">
                {/* ✅ Alerts */}
                {message.status && (
                  <div
                    className={`alert alert-${message.reason === 'error' ? 'danger' : 'success'} alert-dismissible fade show mb-20`}
                    role="alert"
                  >
                    {message.desc}
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setMessage({ ...message, status: false })}
                      aria-label="Close"
                    ></button>
                  </div>
                )}

                {/* ✅ Form */}
                <form onSubmit={handleSubmit}>
                  {/* Supplier Dropdown */}
                  <div className="mb-20">
                    <label htmlFor="supplier_id" className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Supplier <span className="text-danger-600">*</span>
                    </label>
                    <select
                      id="supplier_id"
                      className="form-select radius-8"
                      value={formValues.supplier_id}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, supplier_id: e.target.value }))}
                    >
                      <option value="">-- Select Supplier --</option>
                      {suppliers.map((supplier) => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.name} {supplier.city ? `(${supplier.city})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Title */}
                  <div className="mb-20">
                    <label htmlFor="title" className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Title <span className="text-danger-600">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      id="title"
                      value={formValues.title}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, title: e.target.value }))}
                      placeholder="Title..."
                    />
                  </div>

                  {/* Buy Price */}
                  <div className="mb-20">
                    <label htmlFor="buy" className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Buy Price <span className="text-danger-600">*</span>
                    </label>
                    <input
                      type="number"
                      className="form-control radius-8"
                      id="buy"
                      value={formValues.costPrice}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, costPrice: e.target.value }))}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  {/* Selling Price */}
                  <div className="mb-20">
                    <label htmlFor="sell" className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Selling Price <span className="text-danger-600">*</span>
                    </label>
                    <input
                      type="number"
                      className="form-control radius-8"
                      id="sell"
                      value={formValues.sellPrice}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, sellPrice: e.target.value }))}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  {/* Quantity */}
                  <div className="mb-20">
                    <label htmlFor="qty" className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Quantity <span className="text-danger-600">*</span>
                    </label>
                    <input
                      type="number"
                      className="form-control radius-8"
                      id="qty"
                      value={formValues.quantity}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, quantity: e.target.value }))}
                      min="1"
                      placeholder="Enter Quantity"
                    />
                  </div>

                  {/* Description */}
                  <div className="mb-20">
                    <label htmlFor="description" className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Description <span className="text-danger-600">*</span>
                    </label>
                    <div
                      ref={quillRef}
                      className={`ql-container ${
                        message.reason === 'error' &&
                        (!formValues.description || formValues.description === '<p><br></p>')
                          ? 'is-invalid'
                          : ''
                      }`}
                    />
                  </div>

                  <div className="d-flex align-items-center justify-content-center gap-3">
                    <button
                      type="submit"
                      className="btn btn-primary border border-primary-400 text-md px-56 py-12 radius-8"
                    >
                      Submit
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditStockLayer;
